import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { getResolvedTheme } from './themeDefaults';
import { resolvePageUrl } from '../../utils/pageUtils';

// Social media platforms (from socialLinks.*)
const SOCIAL_PLATFORMS = {
  youtube:   { icon: 'fab fa-youtube',   url: (u) => `https://youtube.com/@${u}` },
  twitter:   { icon: 'fab fa-x-twitter', url: (u) => `https://x.com/${u}` },
  instagram: { icon: 'fab fa-instagram', url: (u) => `https://instagram.com/${u}` },
  tiktok:    { icon: 'fab fa-tiktok',    url: (u) => `https://tiktok.com/@${u}` },
  twitch:    { icon: 'fab fa-twitch',    url: (u) => `https://twitch.tv/${u}` },
  facebook:  { icon: 'fab fa-facebook',  url: (u) => `https://facebook.com/${u}` },
  linkedin:  { icon: 'fab fa-linkedin',  url: (u) => `https://linkedin.com/in/${u}` },
  github:    { icon: 'fab fa-github',    url: (u) => `https://github.com/${u}` },
  discord:   { icon: 'fab fa-discord',   url: (u) => `https://discord.gg/${u}` },
};

// Chess profile platforms (from links.*)
const CHESS_PLATFORMS = {
  chessCom: { icon: 'fas fa-chess-pawn',    url: (u) => `https://chess.com/member/${u}`,       label: 'Chess.com' },
  lichess:  { icon: 'fas fa-chess-knight', url: (u) => `https://lichess.org/@/${u}`,          label: 'Lichess' },
  fideId:   { icon: 'fas fa-chess-board',  url: (u) => `https://ratings.fide.com/profile/${u}`, label: 'FIDE' },
};

/**
 * WebsiteFooter - Rich unified footer for all profile pages.
 * Renders logo, name, tagline, social icons, nav links, copyright, and powered-by.
 * Auto-derives nav links and social from profile data — no user toggle needed.
 */
const WebsiteFooter = ({
  footerEmail = '',
  footerTagline = '',
  profileName = '',
  profileLogo = null,
  username = '',
  theme = {},
  socialLinks = {},
  links = {},
  headerNavLinks = [],
  hasCoachingBlock = false,
  hasLibraryContent = false,
  isOnOwnDomain = false,
}) => {
  const currentYear = new Date().getFullYear();

  const resolved = getResolvedTheme(theme);
  const isDark = resolved.preset === 'dark';
  const accent = resolved.colors.accent || '#749555';
  const pageBg = resolved.colors.pageBackground || (isDark ? '#0d0d0d' : '#ffffff');
  const footerBg = pageBg;
  const mutedColor = resolved.colors.mutedColor || (isDark ? '#94a3b8' : '#6b7280');
  const headingColor = resolved.colors.headingColor || (isDark ? '#ffffff' : '#1a1a2e');
  const copyrightColor = isDark ? '#4b5563' : '#9ca3af';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';
  const borderColorLight = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
  const emailColor = isDark ? accent : headingColor;

  const hasEmail = footerEmail && footerEmail.trim();
  const hasTagline = footerTagline && footerTagline.trim();

  // Build nav links — page links only (skip scroll-to-block links)
  const pageNavLinks = headerNavLinks
    .filter(link => link.pageSlug && link.pageSlug !== '__coaching' && link.pageSlug !== '__library')
    .filter(link => link.pageSlug !== '__blog' || true); // keep blog

  // Separate regular vs blog (same ordering as header)
  const regularLinks = pageNavLinks.filter(link => link.pageSlug !== '__blog');
  const blogLinks = pageNavLinks.filter(link => link.pageSlug === '__blog');

  // Social icons — only render platforms with values
  const activeSocials = Object.entries(socialLinks || {})
    .filter(([platform, value]) => value && SOCIAL_PLATFORMS[platform])
    .map(([platform, value]) => ({ platform, value, ...SOCIAL_PLATFORMS[platform] }));

  // Chess profile icons — from links.* (Chess.com, Lichess, FIDE)
  const activeChess = Object.entries(links || {})
    .filter(([platform, value]) => value && CHESS_PLATFORMS[platform])
    .map(([platform, value]) => ({ platform, value, ...CHESS_PLATFORMS[platform] }));

  const allIcons = [...activeSocials, ...activeChess];

  // Get initials for fallback logo
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const accentGradient = resolved.colors.accentGradient;
  const getButtonBackground = () => {
    if (accentGradient) return accentGradient;
    return `linear-gradient(135deg, ${accent} 0%, ${adjustColor(accent, -15)} 100%)`;
  };

  const hasNavLinks = regularLinks.length > 0 || blogLinks.length > 0 || hasCoachingBlock || hasLibraryContent;

  return (
    <>
      <footer className="wf-footer">
        <div className="wf-container">
          {/* Top section: Brand + Nav */}
          <div className="wf-top">
            {/* Left: Brand */}
            <div className="wf-brand">
              <div className="wf-brand-identity">
                {profileLogo ? (
                  <img src={profileLogo} alt={profileName} className="wf-logo" />
                ) : (
                  <div className="wf-logo-fallback" style={{ background: getButtonBackground() }}>
                    {getInitials(profileName)}
                  </div>
                )}
                <span className="wf-name" style={{ color: headingColor }}>{profileName}</span>
              </div>
              {hasTagline && (
                <p className="wf-tagline">{footerTagline}</p>
              )}
              {hasEmail && (
                <a href={`mailto:${footerEmail}`} className="wf-email">
                  <Mail size={14} />
                  <span>{footerEmail}</span>
                </a>
              )}
            </div>

            {/* Right: Nav links + Social icons */}
            <div className="wf-right">
              {hasNavLinks && (
                <nav className="wf-nav">
                  {regularLinks.map((link) => (
                    <Link
                      key={`page-${link.pageSlug}`}
                      to={resolvePageUrl(link.pageSlug, username, isOnOwnDomain)}
                      className="wf-nav-link"
                    >
                      {link.label}
                    </Link>
                  ))}
                  {blogLinks.map((link) => (
                    <Link
                      key={`page-${link.pageSlug}`}
                      to={resolvePageUrl(link.pageSlug, username, isOnOwnDomain)}
                      className="wf-nav-link"
                    >
                      {link.label}
                    </Link>
                  ))}
                  {hasCoachingBlock && (
                    <Link to={resolvePageUrl('__coaching', username, isOnOwnDomain)} className="wf-nav-link">Coaching</Link>
                  )}
                  {hasLibraryContent && (
                    <Link to={resolvePageUrl('__library', username, isOnOwnDomain)} className="wf-nav-link">Library</Link>
                  )}
                </nav>
              )}
              {allIcons.length > 0 && (
                <div className="wf-socials">
                  {allIcons.map(({ platform, value, icon, url }) => (
                    <a
                      key={platform}
                      href={url(value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wf-social-icon"
                      aria-label={platform}
                    >
                      <i className={icon}></i>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bottom: Copyright + Powered by */}
          <div className="wf-bottom">
            <span className="wf-copyright" style={{ color: copyrightColor }}>
              {profileName ? `© ${currentYear} ${profileName}` : `© ${currentYear}`}
            </span>
            <a
              href="https://chessweb.site"
              target="_blank"
              rel="noopener noreferrer"
              className="wf-powered"
              style={{ color: copyrightColor }}
            >
              Powered by ChessWeb.site
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        .wf-footer {
          background: ${footerBg};
          border-top: 1px solid ${borderColor};
          padding: 40px 0 24px;
          margin-top: auto;
        }

        .wf-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .wf-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 40px;
        }

        /* Brand section */
        .wf-brand {
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-width: 0;
        }

        .wf-brand-identity {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .wf-logo {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .wf-logo-fallback {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          font-weight: 700;
          color: white;
          flex-shrink: 0;
        }

        .wf-name {
          font-size: 1.1rem;
          font-weight: 600;
        }

        .wf-tagline {
          font-size: 0.9rem;
          color: ${mutedColor};
          margin: 0;
          max-width: 400px;
          line-height: 1.5;
        }

        .wf-email {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: ${emailColor};
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 500;
          transition: opacity 0.2s ease;
        }

        .wf-email:hover {
          opacity: 0.8;
        }

        /* Social icons */
        .wf-socials {
          display: flex;
          gap: 8px;
          margin-top: 4px;
        }

        .wf-social-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          color: ${mutedColor};
          background: ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
          text-decoration: none;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .wf-social-icon:hover {
          color: ${headingColor};
          background: ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
        }

        /* Right column */
        .wf-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 12px;
        }

        /* Nav links */
        .wf-nav {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          justify-content: flex-end;
        }

        .wf-nav-link {
          color: ${mutedColor};
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
          padding: 6px 12px;
          border-radius: 6px;
          transition: color 0.2s ease, background 0.2s ease;
        }

        .wf-nav-link:hover {
          color: ${headingColor};
          background: ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
        }

        /* Bottom bar */
        .wf-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 20px;
          border-top: 1px solid ${borderColorLight};
        }

        .wf-copyright {
          font-size: 0.8rem;
          color: ${copyrightColor};
        }

        .wf-powered {
          font-size: 0.8rem;
          color: ${copyrightColor};
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .wf-powered:hover {
          color: ${mutedColor};
        }

        /* Mobile */
        @media (max-width: 768px) {
          .wf-top {
            flex-direction: column;
            gap: 24px;
          }

          .wf-brand {
            align-items: center;
            text-align: center;
            width: 100%;
          }

          .wf-brand-identity {
            justify-content: center;
          }

          .wf-tagline {
            max-width: 100%;
          }

          .wf-right {
            align-items: center;
            width: 100%;
          }

          .wf-socials {
            justify-content: center;
          }

          .wf-nav {
            justify-content: center;
          }

          .wf-bottom {
            flex-direction: column;
            gap: 8px;
            text-align: center;
          }
        }

        @media (max-width: 480px) {
          .wf-footer {
            padding: 32px 0 20px;
          }

          .wf-container {
            padding: 0 16px;
          }

          .wf-nav-link {
            padding: 6px 8px;
            font-size: 0.85rem;
          }
        }
      `}</style>
    </>
  );
};

function adjustColor(hex, percent) {
  if (!hex || !hex.startsWith('#')) return hex;
  const num = parseInt(hex.slice(1), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

function darkenColor(hex, amount) {
  return adjustColor(hex, -amount);
}

function lightenColor(hex, amount) {
  return adjustColor(hex, amount);
}

export default WebsiteFooter;
